import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import useAuthenticatedAxios from '../../auth/AuthenticatedAxios';
import { useToast } from '../../Context/ToastContext';
import { useConfirm } from '../../Context/ConfirmContext';
import { BsFileEarmarkPdf, BsFileEarmarkWord, BsTrash, BsUpload, BsPlus } from 'react-icons/bs';

const Container = styled.div`
  background: rgba(99, 102, 241, 0.05);
  border: none;
  border-radius: 12px;
  border-left: 3px solid rgba(99, 102, 241, 0.5);
  padding: 24px;
  margin-bottom: 20px;
  backdrop-filter: blur(8px);
`;

const SectionTitle = styled.h2`
  color: #fff;
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 20px;
  padding: 10px 18px;
  display: inline-block;
  border: 1px solid rgba(99, 102, 241, 0.3);
  border-radius: 10px;
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(139, 92, 246, 0.1) 100%);
  letter-spacing: 0.3px;
`;

const Description = styled.p`
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 20px;
  font-size: 14px;
  line-height: 1.6;
`;

const TemplateGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
  margin-top: 16px;
`;

const TemplateCard = styled.div`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 12px;
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(99, 102, 241, 0.1);
    border-color: rgba(99, 102, 241, 0.4);
  }
`;

const FileIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 10px;
  background: ${props => props.type === 'pdf' 
    ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' 
    : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 24px;
  flex-shrink: 0;
`;

const TemplateInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const TemplateName = styled.div`
  color: #fff;
  font-weight: 600;
  font-size: 15px;
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TemplateDetails = styled.div`
  color: rgba(255, 255, 255, 0.6);
  font-size: 12px;
  display: flex;
  gap: 12px;
`;

const DeleteButton = styled.button`
  background: transparent;
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 8px;
  padding: 8px;
  cursor: pointer;
  color: rgba(239, 68, 68, 0.8);
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(239, 68, 68, 0.1);
    border-color: rgba(239, 68, 68, 0.5);
    color: #ef4444;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const UploadArea = styled.div`
  border: 2px dashed rgba(99, 102, 241, 0.3);
  border-radius: 12px;
  padding: 32px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
  background: rgba(99, 102, 241, 0.02);

  &:hover {
    border-color: rgba(99, 102, 241, 0.5);
    background: rgba(99, 102, 241, 0.05);
  }

  &.dragover {
    border-color: rgba(99, 102, 241, 0.8);
    background: rgba(99, 102, 241, 0.1);
  }
`;

const UploadIcon = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.2) 0%, rgba(139, 92, 246, 0.2) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
  color: #6366f1;
  font-size: 28px;
`;

const UploadText = styled.div`
  color: rgba(255, 255, 255, 0.9);
  font-size: 16px;
  margin-bottom: 8px;
`;

const UploadHint = styled.div`
  color: rgba(255, 255, 255, 0.5);
  font-size: 13px;
`;

const HiddenInput = styled.input`
  display: none;
`;

const Button = styled.button`
  background: ${props => props.variant === 'primary' 
    ? 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' 
    : 'rgba(99, 102, 241, 0.15)'};
  color: #fff;
  border: ${props => props.variant === 'primary' ? 'none' : '1px solid rgba(99, 102, 241, 0.3)'};
  border-radius: 8px;
  padding: 10px 18px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid #FFFFFF;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: rgba(255, 255, 255, 0.6);
`;

const EmptyIcon = styled.div`
  font-size: 48px;
  margin-bottom: 16px;
  opacity: 0.5;
`;

const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

const SignatureTemplates = () => {
  const apiClient = useAuthenticatedAxios();
  const { showToast } = useToast();
  const { confirm } = useConfirm();
  const fileInputRef = useRef(null);

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [dragover, setDragover] = useState(false);

  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/signature-templates');
      setTemplates(response.data.templates || []);
    } catch (err) {
      console.error('Error fetching signature templates:', err);
      showToast && showToast('Failed to load signature templates', 'error');
    } finally {
      setLoading(false);
    }
  }, [apiClient, showToast]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const handleFileSelect = async (file) => {
    if (!file) return;

    // Validate file type
    const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!allowedTypes.includes(file.type)) {
      showToast && showToast('Only PDF and Word documents are allowed', 'error');
      return;
    }

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      showToast && showToast('File size must be less than 10MB', 'error');
      return;
    }

    try {
      setUploading(true);
      
      const formData = new FormData();
      formData.append('file', file);
      formData.append('name', file.name.replace(/\.[^/.]+$/, '')); // Name without extension

      const response = await apiClient.post('/signature-templates', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setTemplates(prev => [response.data.template, ...prev]);
      showToast && showToast('Template uploaded successfully', 'success');
    } catch (err) {
      console.error('Error uploading template:', err);
      showToast && showToast('Failed to upload template', 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (template) => {
    const confirmed = await confirm({
      title: 'Delete Template',
      message: `Are you sure you want to delete "${template.name}"? This cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      type: 'warning'
    });

    if (!confirmed) return;

    try {
      setDeleting(template.id);
      await apiClient.delete(`/signature-templates/${template.id}`);
      setTemplates(prev => prev.filter(t => t.id !== template.id));
      showToast && showToast('Template deleted', 'success');
    } catch (err) {
      console.error('Error deleting template:', err);
      showToast && showToast('Failed to delete template', 'error');
    } finally {
      setDeleting(null);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragover(false);
    const file = e.dataTransfer.files[0];
    handleFileSelect(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragover(true);
  };

  const handleDragLeave = () => {
    setDragover(false);
  };

  const handleInputChange = (e) => {
    const file = e.target.files[0];
    handleFileSelect(file);
    e.target.value = ''; // Reset input
  };

  return (
    <Container>
      <SectionTitle>Signature Templates</SectionTitle>

      <Description>
        Upload documents that you frequently send for signature. These templates can be quickly 
        attached when sending emails to leads, making it easy to request signatures via DocuSign.
      </Description>

      {/* Upload Area */}
      <UploadArea
        className={dragover ? 'dragover' : ''}
        onClick={() => fileInputRef.current?.click()}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
      >
        <UploadIcon>
          {uploading ? <LoadingSpinner /> : <BsUpload />}
        </UploadIcon>
        <UploadText>
          {uploading ? 'Uploading...' : 'Drop a file here or click to upload'}
        </UploadText>
        <UploadHint>
          PDF or Word documents, max 10MB
        </UploadHint>
        <HiddenInput
          ref={fileInputRef}
          type="file"
          accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          onChange={handleInputChange}
          disabled={uploading}
        />
      </UploadArea>

      {/* Templates List */}
      {loading ? (
        <EmptyState>
          <LoadingSpinner style={{ width: 32, height: 32, margin: '0 auto' }} />
          <div style={{ marginTop: 16 }}>Loading templates...</div>
        </EmptyState>
      ) : templates.length === 0 ? (
        <EmptyState>
          <EmptyIcon>
            <BsFileEarmarkPdf />
          </EmptyIcon>
          <div>No signature templates yet</div>
          <div style={{ fontSize: 13, marginTop: 8 }}>
            Upload your first document to get started
          </div>
        </EmptyState>
      ) : (
        <TemplateGrid>
          {templates.map(template => (
            <TemplateCard key={template.id}>
              <FileIcon type={template.fileType}>
                {template.fileType === 'pdf' ? <BsFileEarmarkPdf /> : <BsFileEarmarkWord />}
              </FileIcon>
              <TemplateInfo>
                <TemplateName title={template.name}>{template.name}</TemplateName>
                <TemplateDetails>
                  <span>{template.fileType.toUpperCase()}</span>
                  <span>{formatFileSize(template.fileSize)}</span>
                  <span>{formatDate(template.createdAt)}</span>
                </TemplateDetails>
              </TemplateInfo>
              <DeleteButton
                onClick={() => handleDelete(template)}
                disabled={deleting === template.id}
                title="Delete template"
              >
                {deleting === template.id ? <LoadingSpinner /> : <BsTrash />}
              </DeleteButton>
            </TemplateCard>
          ))}
        </TemplateGrid>
      )}
    </Container>
  );
};

export default SignatureTemplates;
